import { APP_NAME } from "@/lib/brand";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, downloadFile, blobErrorDetail, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet, Download, Send, Lock, Archive, Check, Upload } from "lucide-react";
import { fmtDate } from "@/lib/workflow";

export function useEconomicFile(projectId) {
  const [ef, setEf] = useState(null);
  const load = useCallback(() => api.get(`/projects/${projectId}/economic-file`).then((r) => setEf(r.data)), [projectId]);
  useEffect(() => { load(); }, [load]);
  return [ef, load];
}

export const EconomicFilePanel = ({ project, onChanged }) => {
  const { user } = useAuth();
  const [ef, reload] = useEconomicFile(project.project_id);
  const [busy, setBusy] = useState(false);
  if (!ef) return null;
  const download = async () => {
    setBusy(true);
    try {
      const name = await downloadFile(`/projects/${project.project_id}/economic-file/template`, `${project.project_number}_Economic_File.xlsx`);
      toast.success(`${name} generated — formulas, sheets and formatting preserved`);
      await reload(); await onChanged();
    } catch (e) { toast.error(await blobErrorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4" data-testid="economic-file-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center"><FileSpreadsheet className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Economic File</div>
            <p className="text-xs text-slate-500">
              {ef.configured ? <>Template <strong>{ef.template_name}</strong> · {ef.mapped_count} field(s) prefilled from project data. Download, complete it in Excel, then tick “Economic File completed”.</> : "No company template configured yet — ask an admin to upload it under Administration → Economic File."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {ef.configured ? (
            <Button onClick={download} disabled={busy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="economic-file-download-template-btn"><Download className="h-4 w-4 mr-2" />{busy ? "Generating…" : "Download prefilled Economic File"}</Button>
          ) : user?.role === "admin" ? (
            <Link to="/admin/economic-file"><Button variant="outline" data-testid="economic-file-configure-link">Configure template</Button></Link>
          ) : <Badge variant="outline" className="text-amber-800 bg-amber-50 border-amber-200">Template not configured</Badge>}
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">The completed workbook stays with you — {APP_NAME} does not parse or validate it. You may still attach it as evidence if you wish.</p>
    </section>
  );
};

function RequestOrderDialog({ project, disabled, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try {
      await api.post(`/projects/${project.project_id}/order-requests`, { notes });
      toast.success("Order request submitted to ERP operators");
      setOpen(false); await onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button className="mt-3 bg-[#2563EB] hover:bg-[#1d4ed8]" disabled={disabled} onClick={() => setOpen(true)} data-testid="erp-request-order-btn"><Send className="h-4 w-4 mr-2" />Request order</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request ERP order — {project.project_number}</DialogTitle><DialogDescription>Creates one internal Order Request for the ERP operators. The ERP order is created manually in your ERP system and then marked as processed in the Order Requests panel — {APP_NAME} does not create ERP orders.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the ERP operator (optional)" data-testid="erp-request-order-notes" />
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="erp-request-order-confirm-btn">Submit request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const StepCard = ({ num, title, done, locked, children }) => (
  <div className={`rounded-lg border p-4 ${done ? "border-emerald-200 bg-emerald-50/40" : locked ? "border-dashed border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={`erp-step-${num}`}>
    <div className="flex items-start gap-3">
      <div className={`h-7 w-7 shrink-0 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`}>
        {done ? <Check className="h-4 w-4" /> : num}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {children}
      </div>
    </div>
  </div>
);

export const ErpHandoverPanel = ({ project, enabled, onChanged }) => {
  const { user } = useAuth();
  const [ef, reload] = useEconomicFile(project.project_id);
  const [arch, setArch] = useState(null);
  const [dl, setDl] = useState(false);
  const [up, setUp] = useState(false);
  const loadArch = useCallback(() => api.get(`/projects/${project.project_id}/archive/status`).then((r) => setArch(r.data)).catch(() => {}), [project.project_id]);
  useEffect(() => { loadArch(); }, [loadArch]);
  if (!ef) return null;

  const order = ef.order_request;
  const efUp = ef.erp_economic_file;
  const canManage = ["admin", "project_coordinator"].includes(user?.role) || project.project_manager_id === user?.user_id;
  const canSubmit = ["admin", "project_coordinator", "project_manager"].includes(user?.role);
  const refresh = async () => { await reload(); await loadArch(); await onChanged(); };

  const downloadArchive = async () => {
    setDl(true);
    try {
      const name = await downloadFile(`/projects/${project.project_id}/archive/download`, `${project.project_number}.zip`);
      toast.success(`Project Archive generated — ${name}`);
      await refresh();
    } catch (e) { toast.error(await blobErrorDetail(e)); } finally { setDl(false); }
  };
  const uploadEf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("phase_key", "erp_handover");
      await api.post(`/projects/${project.project_id}/economic-file`, fd);
      toast.success("Completed Economic File uploaded");
      await refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); } finally { setUp(false); e.target.value = ""; }
  };

  const archState = arch?.state;
  const archiveDone = archState === "completed";
  const archiveOutdated = archState === "outdated" || arch?.outdated;
  const efDone = !!efUp;
  const allReady = enabled && archiveDone && !archiveOutdated && efDone;

  return (
    <div className="space-y-3" data-testid="erp-handover-gate">
      <p className="overline">ERP Handover — final gate</p>

      <StepCard num={1} title="Project Archive" done={archiveDone && !archiveOutdated}>
        <p className="mt-0.5 text-xs text-slate-500">Download the structured ZIP ({project.project_number} — 11 standard folders) before sending to ERP.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="archive-status">
          {archiveOutdated
            ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">⚠ Archive outdated — new download required</Badge>
            : archiveDone
              ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Downloaded</Badge>
              : <Badge variant="outline" className="text-slate-600">Not downloaded</Badge>}
          {arch?.downloaded_at && <span className="text-xs text-slate-500">by {arch.downloaded_by} · {fmtDate(arch.downloaded_at)}</span>}
        </div>
        {canManage
          ? <Button onClick={downloadArchive} disabled={dl} size="sm" className="mt-2 bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="archive-download-btn"><Archive className="h-4 w-4 mr-2" />{dl ? "Preparing…" : archiveDone && !archiveOutdated ? "Download again" : "Download Project Archive"}</Button>
          : <div className="mt-2"><Badge variant="outline" className="text-slate-500">PM / Coordinator / Admin only</Badge></div>}
      </StepCard>

      <StepCard num={2} title="Completed Economic File" done={efDone} locked={false}>
        <p className="mt-0.5 text-xs text-slate-500">Upload the finished Economic File (.xlsx) for this order. Required before requesting the order.</p>
        {efDone && (
          <div className="mt-2 text-sm text-slate-700 flex items-center gap-2" data-testid="erp-economic-current">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
            <span className="font-medium">{efUp.file_name}</span>
            <span className="text-xs text-slate-500">rev {efUp.revision} · {efUp.uploaded_by_name} · {fmtDate(efUp.uploaded_at)}</span>
          </div>
        )}
        {canManage && (
          <label className="mt-2 inline-flex">
            <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={uploadEf} data-testid="erp-economic-upload-input" />
            <Button asChild size="sm" variant={efDone ? "outline" : "default"} className={efDone ? "" : "bg-[#2563EB] hover:bg-[#1d4ed8]"} disabled={up}>
              <span data-testid="erp-economic-upload-btn"><Upload className="h-4 w-4 mr-2" />{up ? "Uploading…" : efDone ? "Replace file" : "Upload Completed Economic File"}</span>
            </Button>
          </label>
        )}
      </StepCard>

      <StepCard num={3} title="Order Request" done={!!order}>
        {order ? (
          <div className="mt-1 text-sm text-slate-700" data-testid="erp-card-order">
            <Badge className={order.status === "processed" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"} data-testid="erp-order-status">{order.status}</Badge>
            <span className="ml-2 text-xs text-slate-500">by {order.requested_by_name} · {fmtDate(order.requested_at)}</span>
            {order.status === "processed" && <div className="text-xs text-slate-600 mt-1">Processed by {order.processed_by_name} · {fmtDate(order.processed_at)}{order.erp_reference && <> · ERP ref <span className="font-mono">{order.erp_reference}</span></>}</div>}
            <Link to="/orders" className="text-xs text-blue-700 inline-block mt-1">Open Order Requests</Link>
          </div>
        ) : (
          <>
            {(!canSubmit || !allReady) && (
              <p className="mt-1 text-xs text-slate-500 flex items-center gap-1" data-testid="erp-order-lock">
                <Lock className="h-3.5 w-3.5 text-slate-400" />
                {!canSubmit ? "Only Project Managers, Coordinators or Admins can request the order."
                  : !enabled ? "Complete every preceding phase to request the order."
                  : "Complete the required steps above (archive + completed Economic File) to request the order."}
              </p>
            )}
            {canSubmit && <RequestOrderDialog project={project} disabled={!allReady} onDone={refresh} />}
          </>
        )}
      </StepCard>
    </div>
  );
};
